import classNames from 'clsx';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { IS_PAID_USER } from 'reactApp/appHelpers/configHelpers';
import { useTBankFromQuery } from 'reactApp/components/TBankModal/useTBankFromQuery';
import { useDuplicatesActivation } from 'reactApp/hooks/duplicates/useDuplicatesActivation';
import { useDuplicatesCount } from 'reactApp/hooks/duplicates/useDuplicatesCount';
import { runDesktopR7Script } from 'reactApp/hooks/useDesktopR7App';
import { setIsFromAttaches } from 'reactApp/modules/attaches/attaches.actions';
import { AttachesSelectors } from 'reactApp/modules/attaches/attaches.selectors';
import { getBillingDataRequest } from 'reactApp/modules/billingData/billingData.module';
import { BillingDataSelectors } from 'reactApp/modules/billingData/billingData.selectors';
import { familyCreateAfterPaid } from 'reactApp/modules/family/family.actions';
import { getCurrentFolderHome, getDomainFoldersFilterActive, isDefaultRootContent } from 'reactApp/modules/home/home.selectors';
import { checkUpdateLicenseRequest } from 'reactApp/modules/profile/profile.module';
import { getIsUserOverquotaProfileLoaded, getUserOverquotaCurrentState } from 'reactApp/modules/profile/profile.selectors';
import { updatePromoManager } from 'reactApp/modules/promoNew/manager';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { useUpgradeSubscription } from 'reactApp/modules/upgradeSubscription/useUpgradeSubscription';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { UserQuotaSelectors } from 'reactApp/modules/userQuota/userQuota.selectors';
import { BreadcrumbsContainer } from 'reactApp/ui/BreadcrumbsContainer/BreadcrumbsContainer';
import { DataList } from 'reactApp/ui/Datalist/DataList';
import BizPageTabMenu from 'reactApp/ui/DataListItemRow/components/BizPageTabMenu/BizPageTabMenu';
import { Footer } from 'reactApp/ui/Footer/Footer';
import { HomePageInformer } from 'reactApp/ui/HomePageInformer/HomePageInformer';
import { WelcomeNewPromo } from 'reactApp/ui/WelcomePromo/WelcomeNewPromo/WelcomeNewPromo';
import { WelcomeOverquotaPromo } from 'reactApp/ui/WelcomePromo/WelcomeNewPromo/WelcomeOverquotaPromo';
import { WelcomePromo } from 'reactApp/ui/WelcomePromo/WelcomePromo';
import { scrollToTop } from 'reactApp/utils/helpers';
import { ECategoryGa, sendPaymentGa } from 'reactApp/utils/paymentGa';

import styles from './HomePage.css';

const AB_MAX_USED_SPACE_PERCENT = 80;
const WAIT_FOR_PROFILE_LOAD_MS = 1000;

// eslint-disable-next-line complexity, max-lines-per-function
export const HomePage: React.VFC<{ initialItemId?: string }> = ({ initialItemId = '' }) => {
    const location = useLocation();
    const timerId = useRef<number | undefined | null>();
    const dispatch = useDispatch();

    const item = useSelector(getCurrentFolderHome);
    const { id } = item || {};
    const isDefaultContent = useSelector(isDefaultRootContent);
    const domainFoldersFilterActive = useSelector(getDomainFoldersFilterActive);
    const isCommonSourceRadar = domainFoldersFilterActive || item?.isMounted;
    const { usedPercent } = useSelector(UserQuotaSelectors.getUserQuotaState);
    const { isLoaded } = useSelector(UserQuotaSelectors.getLoadingState);
    const isFromAttaches = useSelector(AttachesSelectors.getIsFromAttaches);
    const isRestoreRequested = useSelector(getUserOverquotaCurrentState) === 'restore_requested';
    const isUserOverquotaProfileLoaded = useSelector(getIsUserOverquotaProfileLoaded);
    const [isShowEmptyDataList, setIsShowEmptyDataList] = useState<boolean | null>(null);
    const isB2bBizUser = useSelector(UserSelectors.isB2BUser);

    const showBizPageTabMenu = isB2bBizUser && !domainFoldersFilterActive && item?.isFolder && item?.childs.length > 0;

    const canShowEmptyDatalist = isDefaultContent && !isB2bBizUser && usedPercent < AB_MAX_USED_SPACE_PERCENT;

    const {
        cloudflags: { FAMILY },
    } = useSelector(UserSelectors.getUserData);
    const { isLoaded: isBillingLoaded, isLoading: isBillingLoading } = useSelector(BillingDataSelectors.getBillingDataLoadingState);
    const hasFamilyFoldersFeature = useSelector(BillingDataSelectors.isUserWithFamilyFolders);
    const hasFamilyAlbumsFeature = useSelector(BillingDataSelectors.isUserWithFamilyAlbums);

    const currentUserEmail = useSelector(UserSelectors.getEmail) ?? '';
    // Дергаем биллинг для проверки
    useEffect(() => {
        if (!isBillingLoading && !isBillingLoaded && !FAMILY && IS_PAID_USER) {
            dispatch(getBillingDataRequest({ radar: 'subs' }));
        }
    }, [isBillingLoading, isBillingLoaded]);

    useTBankFromQuery();

    useUpgradeSubscription();

    useDuplicatesCount();
    useDuplicatesActivation();

    useEffect(() => {
        if (isBillingLoaded && hasFamilyFoldersFeature && hasFamilyAlbumsFeature && !FAMILY) {
            dispatch(familyCreateAfterPaid());
        }
    }, [isBillingLoaded]);

    useEffect(() => {
        if (!canShowEmptyDatalist || !isLoaded) {
            return;
        }

        if (isUserOverquotaProfileLoaded || isRestoreRequested) {
            if (timerId.current) {
                clearTimeout(timerId.current);
                timerId.current = null;
            }
            setIsShowEmptyDataList(isRestoreRequested);
            return;
        }

        if (!timerId.current) {
            // сначала ждем прогрузки профиля либо таймаут, чтобы не моргали велком промо
            timerId.current = window.setTimeout(() => setIsShowEmptyDataList(false), WAIT_FOR_PROFILE_LOAD_MS);
        }
    }, [canShowEmptyDatalist, isRestoreRequested, isLoaded, isUserOverquotaProfileLoaded]);

    useEffect(() => {
        sendPaymentGa({ eventCategory: ECategoryGa.entered, action: isCommonSourceRadar ? 'common' : 'home' });
        sendPaymentGa({
            eventCategory: ECategoryGa.entered,
            action: 'page-entered',
            source: isFromAttaches ? 'from-mail' : isCommonSourceRadar ? 'common' : 'home',
            source_to: isCommonSourceRadar ? 'common' : 'home',
        });

        return () => {
            if (isFromAttaches) {
                dispatch(setIsFromAttaches(false));
            }
        };
    }, [isFromAttaches, isCommonSourceRadar]);

    useEffect(() => {
        if (isDefaultContent) {
            dispatch(checkUpdateLicenseRequest());
        }
    }, [isDefaultContent]);

    useEffect(() => {
        // TODO: routing
        // Без preventPush в историю браузера добавляется лишние айтемы с тем же адресом.
        // Но с ним баг гораздо хуже - при подьеме из внутренней папки через кнопку Бек браузера,
        // урл в строке не соответствует содержимому даталиста
        // if (preventPush) {
        //     console.log('dispatch change hist from home: revert preventPush ', location.pathname, location);
        //     dispatch(preventHistoryPush(false));
        //     return;
        // }
    }, [location.pathname]);

    useEffect(() => {
        scrollToTop();
    }, [id]);

    useLayoutEffect(() => {
        // Вызываем `.update()`, чтобы уведомить менеджер промо о необходимости перерасчета
        updatePromoManager();
        runDesktopR7Script({ email: currentUserEmail, userId: currentUserEmail });
    }, []);

    // Если canShowEmptyDatalist, то сначала ждем прогрузки профиля либо таймаут, чтобы не моргали велком промо
    const finalIsShowEmptyDataList = isShowEmptyDataList || (canShowEmptyDatalist && isShowEmptyDataList === null);

    if (isDefaultContent && !finalIsShowEmptyDataList) {
        if (usedPercent < AB_MAX_USED_SPACE_PERCENT) {
            return (
                <div className={styles.promo}>
                    <WelcomeNewPromo />
                    <Footer storage={EStorageType.home} isAbsolutePos={true} withMargins />
                </div>
            );
        }

        if (usedPercent >= AB_MAX_USED_SPACE_PERCENT) {
            return (
                <div className={styles.promo}>
                    <WelcomeOverquotaPromo />
                    <Footer storage={EStorageType.home} isAbsolutePos={true} withMargins />
                </div>
            );
        }

        if (!isLoaded) {
            return null;
        }
    }

    if (isDefaultContent && !domainFoldersFilterActive && !finalIsShowEmptyDataList) {
        return (
            <div className={styles.promo}>
                <WelcomePromo />
                <Footer storage={EStorageType.home} isAbsolutePos={true} withMargins />
            </div>
        );
    }

    return (
        <div className={styles.root}>
            <HomePageInformer />

            {isB2bBizUser && (
                <BreadcrumbsContainer
                    className={classNames(styles.breadcrumbs, { [styles.breadcrumbs_without_tab_menu]: !showBizPageTabMenu })}
                />
            )}

            {showBizPageTabMenu && (
                <div className={styles.bizPageTabMenu}>
                    <BizPageTabMenu />
                </div>
            )}
            <DataList initialItemId={initialItemId} showBreadcrumbs={!isB2bBizUser} />
        </div>
    );
};
